import { useMemo } from "react";
import classNames from "classnames";
import { Validation, ValidationTypes } from "../../common/validation/constants";
import useEntityAnnotation from "../../configMap/hooks/useEntityAnnotation";
import {
  Component,
  GroupBlockEntityType,
  useValidation,
} from "../../configMap";
import { Annotation } from "../../annotator/interfaces/annotation";
import NUMBER_VALIDATIONS from "../../common/entityComponents/components/number/validations";
import DATE_VALIDATIONS from "../../common/entityComponents/components/date/validations";
import WEEK_VALIDATIONS from "../../common/entityComponents/components/week/validations";
import { ValidationStatuses } from "../interfaces/validation";
import useEntityValidationMessage from "./useEntityValidationMessage";
import { EntityAnnotation } from "../../annotation";
import { NormalizationErrorType } from "../../annotation/interfaces/annotation";

const useAnnotationValidation = (
  groupBlockEntityType: GroupBlockEntityType,
  annotationsForEntity: Array<Annotation>,
  groupBlockIndex: number,
  requiresAttention: boolean,
) => {
  const { entityType } = groupBlockEntityType;

  const entityAnnotation: EntityAnnotation | undefined = useMemo(() => {
    let annotation;

    if (annotationsForEntity.length >= 1) {
      annotation = annotationsForEntity[0];
    }

    if (annotation) {
      return {
        ...annotation,
        index: annotation.index!,
        pageTokenIndices: annotation.pageTokenIndices!,
        value: annotation.values?.join(" ") || "",
        modelScore: annotation.modelScore!,
        isByUser: annotation.isByUser!,
      };
    }

    return undefined;
  }, [annotationsForEntity]);

  const { value, normalizedValue, options, errorType } =
    useEntityAnnotation(entityAnnotation);

  const textValue = useMemo(
    () => normalizedValue ?? value,
    [value, normalizedValue],
  );

  const hasNormalizedValue = useMemo(
    () => !!normalizedValue,
    [normalizedValue],
  );

  const mergedValidations = useMemo((): Array<Validation> => {
    const output: Array<Validation> = [];
    switch (entityType.component.toLowerCase()) {
      case Component.NUMBER.toLowerCase():
        return [...output, ...NUMBER_VALIDATIONS];
      case Component.DATE.toLowerCase():
        return [...output, ...DATE_VALIDATIONS];
      case Component.WEEK.toLowerCase():
        return [...output, ...WEEK_VALIDATIONS];
      case Component.INPUT.toLowerCase():
      default:
        return output;
    }
  }, [entityType.component]);

  const validation = useValidation(
    textValue,
    `${entityType.id}-${groupBlockIndex}`,
    mergedValidations,
  );

  const isValidOption = useMemo(() => {
    if (!options?.length || !normalizedValue) {
      return true;
    }

    const lowercasedOptions = [...options].map((o) => o.toLowerCase());
    return lowercasedOptions.includes(normalizedValue.toLowerCase());
  }, [normalizedValue, options]);

  const hasInvalidNormalizedValue = useMemo(() => {
    if (!hasNormalizedValue) {
      return false;
    }

    return (
      errorType === NormalizationErrorType.INVALID_VALUE ||
      errorType === NormalizationErrorType.ERROR_WHILE_NORMALIZING
    );
  }, [hasNormalizedValue, errorType]);

  const entityAnnotations: Array<EntityAnnotation> = useMemo(() => {
    return annotationsForEntity.map((annotation) => ({
      ...annotation,
      index: annotation.index!,
      pageTokenIndices: annotation.pageTokenIndices!,
      value: annotation.values?.join(" ") || "",
      modelScore: annotation.modelScore!,
      isByUser: annotation.isByUser!,
    }));
  }, [annotationsForEntity]);

  const validationStatuses: ValidationStatuses = useMemo(
    () => ({
      isError: validation.type === ValidationTypes.ERROR || !isValidOption,
      isWarning: validation.type === ValidationTypes.WARNING,
      isInfo: validation.type === ValidationTypes.INFO,
      isInvalid: hasInvalidNormalizedValue,
      requiresAttention,
    }),
    [validation, hasInvalidNormalizedValue, requiresAttention, isValidOption],
  );

  const validationClasses = classNames({
    "error-status": validationStatuses.isError,
    "warning-status": validationStatuses.isWarning,
    "info-status": validationStatuses.isInfo,
    invalid: validationStatuses.isInvalid,
    "requires-attention": validationStatuses.requiresAttention,
  });

  const validationMessage = useEntityValidationMessage(
    validation,
    isValidOption,
    options,
    entityAnnotation?.entityAnnotationNormalization?.errorMessage,
  );

  return {
    validationClasses,
    validation,
    mergedValidations,
    value,
    normalizedValue,
    options,
    entityAnnotations,
    validationMessage,
  };
};

export default useAnnotationValidation;
